import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';

import type { Bounds, Filters } from '../lib/consts';
import { alertCriteriaRows, buildAlertCriteria, createEmailAlert, isBroadAlert } from '../lib/alerts';
import { Button } from './ui/Button';

type AlertSignupModalProps = {
  filters: Filters;
  selectedLocation: string | null;
  bounds: Bounds | null;
  displayCurrency: string;
  onClose: () => void;
};

export function AlertSignupModal({ filters, selectedLocation, bounds, displayCurrency, onClose }: AlertSignupModalProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const criteria = useMemo(
    () => buildAlertCriteria(email, filters, selectedLocation, bounds, displayCurrency),
    [bounds, displayCurrency, email, filters, selectedLocation],
  );
  const criteriaRows = alertCriteriaRows(criteria);
  const broadAlert = isBroadAlert(criteria);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setStatus('submitting');
    setErrorMessage('');
    if (broadAlert) {
      setStatus('error');
      setErrorMessage('Choose a max price, bedrooms, or keywords before creating an alert.');
      return;
    }
    try {
      await createEmailAlert(criteria);
      setStatus('submitted');
    } catch (error) {
      console.error('Failed to create alert:', error);
      setStatus('error');
      setErrorMessage('Could not create this alert. Please check the email address and try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-16">
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {status === 'submitted' ? (
          <div className="space-y-4">
            <EnvelopeIcon className="h-8 w-8 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Check your email</h2>
              <p className="mt-2 text-sm text-gray-600">
                We sent a confirmation link to {email}. Alerts start after you confirm.
              </p>
            </div>
            <Button type="button" onClick={onClose} className="w-full">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Create email alert</h2>
              <p className="mt-2 text-sm text-gray-600">
                We’ll email you when new listings match these criteria.
              </p>
            </div>

            <dl className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
              {criteriaRows.map(row => (
                <div key={row.label} className="flex justify-between gap-4 py-1">
                  <dt className="text-gray-500">{row.label}</dt>
                  <dd className="text-right font-medium text-gray-900">{row.value}</dd>
                </div>
              ))}
            </dl>

            <label className="block text-sm font-medium text-gray-700">
              Email address
              <input
                type="email"
                required
                value={email}
                onChange={event => setEmail(event.target.value)}
                className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="you@example.com"
              />
            </label>

            {status === 'error' && <p className="text-sm text-red-600">{errorMessage}</p>}
            {broadAlert && status !== 'error' && (
              <p className="text-sm text-amber-700">
                Choose a max price, bedrooms, or keywords before creating an alert.
              </p>
            )}

            <div className="flex gap-3">
              <Button type="submit" disabled={status === 'submitting' || broadAlert} className="flex-1">
                {status === 'submitting' ? 'Sending...' : 'Send confirmation'}
              </Button>
              <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
